import * as React from 'react';
import {
  Star,
  Search,
  X,
  ArrowUp,
  ArrowDown,
  Filter,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Separator } from '@/components/ui/separator';
import type { CostCentre } from '@/types';
import { PROJECT_STATUSES } from '@/lib/statuses';

type EnrichedCostCentre = CostCentre & {
  isFavourite?: boolean;
  lastAccessedDate?: Date;
};

interface CostCentreSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  costCentres: EnrichedCostCentre[];
  value?: string;
  onSelect: (costCentreId: string) => void;
  onToggleFavourite: (costCentreId: string) => void;
  isFavourite: (costCentreId: string) => boolean;
  selectedCostCentreId?: string;
  configuredStatuses?: string[];
}

type SortColumn = 'code' | 'name' | 'description' | 'department' | 'status';
type SortDirection = 'asc' | 'desc' | null;

const SEARCH_DEBOUNCE_MS = 300;
const ITEMS_PER_PAGE = 50;

export function CostCentreSearchModal({
  open,
  onOpenChange,
  costCentres,
  value,
  onSelect,
  onToggleFavourite,
  isFavourite,
  selectedCostCentreId,
  configuredStatuses,
}: CostCentreSearchModalProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = React.useState(false);
  const [displayedItems, setDisplayedItems] = React.useState(ITEMS_PER_PAGE);

  const [pendingShowFavouritesOnly, setPendingShowFavouritesOnly] = React.useState(false);
  const [pendingStatusFilter, setPendingStatusFilter] = React.useState<string[]>([]);
  const [pendingDepartmentFilter, setPendingDepartmentFilter] = React.useState<string[]>([]);

  const [showFavouritesOnly, setShowFavouritesOnly] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [departmentFilter, setDepartmentFilter] = React.useState<string[]>([]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Determine which statuses to offer in the filter dropdown
  const availableStatusOptions = React.useMemo(() => {
    if (!configuredStatuses || configuredStatuses.length === 0) {
      return PROJECT_STATUSES;
    }
    return PROJECT_STATUSES.filter(s => configuredStatuses.includes(s.value));
  }, [configuredStatuses]);

  // Pre-fill search with selected cost centre name when modal opens with a selection
  React.useEffect(() => {
    if (open && selectedCostCentreId) {
      const selectedCC = costCentres.find(cc => String(cc.id) === String(selectedCostCentreId));
      if (selectedCC) {
        setSearchQuery(selectedCC.name);
      }
    }
  }, [open, selectedCostCentreId, costCentres]);

  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setSortColumn('name');
      setSortDirection('asc');
      setShowFilters(false);
      setDisplayedItems(ITEMS_PER_PAGE);
      setShowFavouritesOnly(false);
      setStatusFilter([]);
      setDepartmentFilter([]);
      setPendingShowFavouritesOnly(false);
      setPendingStatusFilter([]);
      setPendingDepartmentFilter([]);
    }
  }, [open]);

  const fuzzyMatch = React.useCallback((text: string, query: string): boolean => {
    if (!text) return false;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();
    if (textLower.includes(queryLower)) return true;
    let queryIndex = 0;
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++;
      }
    }
    return queryIndex === queryLower.length;
  }, []);

  const uniqueDepartments = React.useMemo(() => {
    const departments = new Set<string>();
    costCentres.forEach(cc => {
      if (cc.department) departments.add(cc.department);
    });
    return Array.from(departments).sort();
  }, [costCentres]);

  const filteredCostCentres = React.useMemo(() => {
    let filtered = [...costCentres];

    if (showFavouritesOnly) {
      filtered = filtered.filter(cc => isFavourite(String(cc.id)));
    }

    // Apply status filter: when no explicit user filter is set, default to configured statuses
    if (statusFilter.length > 0) {
      filtered = filtered.filter(cc => statusFilter.includes(cc.status));
    } else if (configuredStatuses && configuredStatuses.length > 0) {
      filtered = filtered.filter(cc => configuredStatuses.includes(cc.status));
    }

    if (departmentFilter.length > 0) {
      filtered = filtered.filter(cc => cc.department && departmentFilter.includes(cc.department));
    }

    if (debouncedSearch) {
      filtered = filtered.filter(costCentre => {
        return (
          fuzzyMatch(costCentre.name, debouncedSearch) ||
          fuzzyMatch(costCentre.code, debouncedSearch) ||
          fuzzyMatch(costCentre.description || '', debouncedSearch) ||
          fuzzyMatch(costCentre.department, debouncedSearch) ||
          fuzzyMatch(costCentre.status, debouncedSearch)
        );
      });
    }

    return filtered;
  }, [costCentres, debouncedSearch, showFavouritesOnly, statusFilter, departmentFilter, fuzzyMatch, isFavourite]);

  const sortedCostCentres = React.useMemo(() => {
    if (!sortDirection) return filteredCostCentres;

    return [...filteredCostCentres].sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortColumn) {
        case 'code':
          aValue = a.code.toLowerCase();
          bValue = b.code.toLowerCase();
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        case 'department':
          aValue = a.department.toLowerCase();
          bValue = b.department.toLowerCase();
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCostCentres, sortColumn, sortDirection]);

  const displayedCostCentres = React.useMemo(() => {
    return sortedCostCentres.slice(0, displayedItems);
  }, [sortedCostCentres, displayedItems]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn('name');
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSelect = (costCentreId: string) => {
    if (String(costCentreId) === String(selectedCostCentreId)) {
      onOpenChange(false);
      return;
    }
    onSelect(costCentreId);
    onOpenChange(false);
  };

  const handleStarClick = (e: React.MouseEvent, costCentreId: string) => {
    e.stopPropagation();
    e.preventDefault();
    onToggleFavourite(costCentreId);
  };

  const handleApplyFilters = () => {
    setShowFavouritesOnly(pendingShowFavouritesOnly);
    setStatusFilter(pendingStatusFilter);
    setDepartmentFilter(pendingDepartmentFilter);
  };

  const handleClearFilters = () => {
    setPendingShowFavouritesOnly(false);
    setPendingStatusFilter([]);
    setPendingDepartmentFilter([]);
    setShowFavouritesOnly(false);
    setStatusFilter([]);
    setDepartmentFilter([]);
  };

  const handleLoadMore = () => {
    setDisplayedItems(prev => prev + ITEMS_PER_PAGE);
  };

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column || !sortDirection) {
      return null;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4" />
    );
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'in progress':
        return 'default';
      case 'planning':
        return 'secondary';
      case 'on hold':
      case 'cancelled':
        return 'destructive';
      case 'completed':
        return 'outline';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="text-base">Select Cost Centre</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden min-h-0">
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <div className="px-4 py-3 border-b shrink-0">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search cost centres..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? "bg-muted" : ""}
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {sortedCostCentres.length} cost centre{sortedCostCentres.length !== 1 ? 's' : ''} found
              </div>
            </div>

            <div className="flex-1 overflow-auto min-h-0">
              <div className="min-w-max px-4">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-[50px] bg-background">
                        <Star className="h-4 w-4 text-muted-foreground" />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[150px]"
                        onClick={() => handleSort('code')}
                      >
                        <div className="flex items-center">
                          Cost Centre Code
                          {renderSortIcon('code')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[200px]"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Name
                          {renderSortIcon('name')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[250px]"
                        onClick={() => handleSort('description')}
                      >
                        <div className="flex items-center">
                          Description
                          {renderSortIcon('description')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[150px]"
                        onClick={() => handleSort('department')}
                      >
                        <div className="flex items-center">
                          Type
                          {renderSortIcon('department')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[120px]"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          {renderSortIcon('status')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedCostCentres.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No cost centres match these results. Please try another filter/search term.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {displayedCostCentres.map((costCentre) => (
                          <TableRow
                            key={costCentre.id}
                            className={cn(
                              'cursor-pointer hover:bg-muted/50',
                              String(costCentre.id) === String(selectedCostCentreId) && 'bg-primary/10'
                            )}
                            onClick={() => handleSelect(String(costCentre.id))}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Star
                                className={cn(
                                  'h-4 w-4 cursor-pointer transition-colors',
                                  isFavourite(String(costCentre.id))
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-muted-foreground hover:text-yellow-400'
                                )}
                                onClick={(e) => handleStarClick(e, String(costCentre.id))}
                              />
                            </TableCell>
                            <TableCell>{costCentre.code}</TableCell>
                            <TableCell className="font-medium">{costCentre.name}</TableCell>
                            <TableCell>{costCentre.description || '-'}</TableCell>
                            <TableCell>{costCentre.department}</TableCell>
                            <TableCell>
                              <Badge variant={getStatusVariant(costCentre.status)}>
                                {PROJECT_STATUSES.find(s => s.value === costCentre.status)?.label || costCentre.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>

                {displayedCostCentres.length < sortedCostCentres.length && (
                  <div className="flex justify-center py-4">
                    <Button variant="outline" onClick={handleLoadMore}>
                      Load More ({sortedCostCentres.length - displayedCostCentres.length} remaining)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {showFilters && (
            <>
              <Separator orientation="vertical" />
              <div className="w-[300px] border-l flex flex-col min-h-0">
                <div className="px-4 py-2.5 border-b flex items-center justify-between shrink-0">
                  <h3 className="font-semibold text-sm">Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setShowFilters(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Favourites</Label>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="cc-favourites"
                          checked={pendingShowFavouritesOnly}
                          onCheckedChange={(checked) => setPendingShowFavouritesOnly(checked === true)}
                        />
                        <label
                          htmlFor="cc-favourites"
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Show only favourites
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Status</Label>
                      <MultiSelect
                        options={availableStatusOptions.map(s => ({ label: s.label, value: s.value }))}
                        value={pendingStatusFilter}
                        onValueChange={setPendingStatusFilter}
                        placeholder="Select statuses..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Type</Label>
                      <MultiSelect
                        options={uniqueDepartments.map(d => ({ label: d, value: d }))}
                        value={pendingDepartmentFilter}
                        onValueChange={setPendingDepartmentFilter}
                        placeholder="Select types..."
                      />
                    </div>
                  </div>
                </div>

                <div className="px-4 py-2.5 border-t space-y-2 shrink-0">
                  <Button className="w-full" size="sm" onClick={handleApplyFilters}>
                    Apply Filters
                  </Button>
                  <Button variant="outline" className="w-full" size="sm" onClick={handleClearFilters}>
                    Clear All Filters
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
